import React, { useEffect, useState } from 'react';
import { paymentService } from '../../services/payment.service';
import { codesService, type TopupVoucherQuote } from '../../services/affiliate.service';
import { FEE_PERCENTAGE, FONT_MONO, FONT_SERIF, PRICE_PER_CREDIT, TOP_UP_OPTIONS } from './landingData';
import { fmtIDR, fmtNum } from './format';

declare global {
    interface Window { snap: any }
}

export type SettleStatus = 'SUCCESS' | 'PENDING' | 'FAILED';

interface Props {
    open: boolean;
    onClose: () => void;
    /** Reported after Midtrans hands control back and the server reconciled the order. */
    onSettled: (status: SettleStatus, message: string) => void;
}

/**
 * The design's top-up modal, wired to the Midtrans Snap flow
 * (create transaction → snap.pay → reconcile). Used by the landing page and
 * the account billing panel.
 */
export const TopUpDialog: React.FC<Props> = ({ open, onClose, onSettled }) => {
    const [amount, setAmount] = useState(25);
    const [customMode, setCustomMode] = useState(false);
    const [customValue, setCustomValue] = useState('');
    const [busy, setBusy] = useState(false);
    // Optional TOPUP_BONUS voucher: checked with "Apply", sent with the payment, and
    // granted by the server only when the payment settles.
    const [voucherInput, setVoucherInput] = useState('');
    const [voucher, setVoucher] = useState<TopupVoucherQuote | null>(null);
    const [voucherErr, setVoucherErr] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount(25); setCustomMode(false); setCustomValue(''); setBusy(false);
            setVoucher(null); setVoucherErr(null); setVoucherInput('');
        }
        document.body.classList.toggle('hv-modal-open', open);
        return () => document.body.classList.remove('hv-modal-open');
    }, [open]);

    if (!open) return null;

    const subtotal = amount * PRICE_PER_CREDIT;
    const fee = subtotal * FEE_PERCENTAGE;
    const total = subtotal + fee;
    // Same rule as the server (CreditsService.bonusFor): % of credits bought, or flat.
    const bonus = voucher ? Math.round((voucher.bonusPercent != null ? (amount * voucher.bonusPercent) / 100 : voucher.bonusCredits) * 1000) / 1000 : 0;

    const applyVoucher = async () => {
        const code = voucherInput.trim().toUpperCase();
        if (!code || checking) return;
        setChecking(true);
        setVoucherErr(null);
        try {
            setVoucher(await codesService.quoteTopup(code, Math.max(amount, 1)));
        } catch (e: any) {
            setVoucher(null);
            setVoucherErr(e?.response?.data?.message || 'Kode tidak bisa dipakai.');
        } finally {
            setChecking(false);
        }
    };
    const removeVoucher = () => {
        setVoucher(null); setVoucherInput(''); setVoucherErr(null);
    };

    const report = (status: SettleStatus, message: string) => { setBusy(false); onSettled(status, message); };

    const pay = async () => {
        if (amount <= 0 || busy) return;
        try {
            setBusy(true);
            const { token, order_id } = await paymentService.createTransaction(total, voucher?.code);
            if (!window.snap) {
                report('FAILED', 'Gerbang pembayaran gagal dimuat. Muat ulang halaman lalu coba lagi.');
                return;
            }
            // Close first: Snap's overlay must not sit under this dialog.
            onClose();
            const reconcile = async () => {
                if (!order_id) return;
                const res = await paymentService.syncStatus(order_id);
                if (res?.status === 'SUCCESS') report('SUCCESS', `Pembayaran diterima. ${res.credits ?? 0} credit sudah masuk${res.bonusCredits ? `, plus bonus ${res.bonusCredits} credit dari voucher` : ''}.`);
                else if (res?.status === 'FAILED') report('FAILED', 'Pembayaran gagal atau kedaluwarsa. Tidak ada credit yang ditambahkan.');
                else report('PENDING', 'Pembayaran belum terkonfirmasi. Tekan "Periksa" pada baris transaksi di Billing setelah beberapa saat.');
            };
            window.snap.pay(token, {
                onSuccess: () => void reconcile(),
                onPending: () => void reconcile(),
                onError: () => report('FAILED', 'Pembayaran gagal. Tidak ada credit yang ditambahkan.'),
                onClose: () => void reconcile(),
            });
        } catch (error: any) {
            console.error('Payment error:', error);
            const msg = error?.response?.data?.message;
            if (voucher && msg) {
                // Voucher became invalid between "Apply" and paying: keep the dialog open.
                setBusy(false); setVoucher(null); setVoucherErr(msg);
                return;
            }
            report('FAILED', 'Gagal memulai pembayaran. Coba lagi.');
        }
    };

    const optBtn = (active: boolean): React.CSSProperties => ({ height: 44, minWidth: 70, padding: '0 20px', borderRadius: 999, fontSize: 14.5, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${active ? 'var(--brand,#13422C)' : 'var(--hv-line)'}`, background: active ? 'var(--hv-mint)' : 'var(--hv-card-2)', color: 'var(--hv-ink)' });

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(20,21,22,0.6)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Top up credits" style={{ width: '100%', maxWidth: 470, maxHeight: '90vh', overflow: 'auto', borderRadius: 26, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 60px 90px -40px rgba(0,0,0,0.5)', animation: 'hvPop 0.4s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '26px 26px 0' }}>
                    <div>
                        <h2 style={{ margin: '8px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.05, letterSpacing: '-0.015em' }}>Top up credits</h2>
                    </div>
                    <div style={{ flex: 1 }} />
                    <button type="button" onClick={onClose} className="hv-h-mint" style={{ width: 34, height: 34, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>
                </div>

                <div style={{ padding: '24px 26px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '14px 0 4px' }}>
                        <span style={{ fontFamily: FONT_SERIF, fontSize: 60, lineHeight: 1, letterSpacing: '-0.02em', fontWeight: 500 }}>{fmtNum(amount)}</span>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.14em', color: 'var(--hv-muted)' }}>CREDITS</span>
                    </div>

                    <div style={{ marginTop: 18, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 9 }}>
                        {TOP_UP_OPTIONS.map((v) => (
                            <button key={v} type="button" onClick={() => { setAmount(v); setCustomMode(false); }} className="hv-h-brand-bd" style={optBtn(!customMode && amount === v)}>{v}</button>
                        ))}
                        <button type="button" onClick={() => { setCustomMode(true); setCustomValue(''); setAmount(0); }} className="hv-h-brand-bd" style={{ ...optBtn(customMode), minWidth: undefined }}>Custom</button>
                    </div>

                    {customMode && (
                        <div style={{ marginTop: 14, width: '100%', display: 'flex', alignItems: 'center', gap: 8, height: 50, padding: '0 16px', borderRadius: 14, border: '1.5px solid var(--brand,#13422C)', background: 'var(--hv-card-2)' }}>
                            <input
                                autoFocus
                                inputMode="numeric"
                                value={customValue}
                                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setCustomValue(v); setAmount(v ? parseInt(v, 10) : 0); }}
                                placeholder="e.g. 75"
                                style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 15, fontWeight: 700, color: 'var(--hv-ink)' }}
                            />
                            <span style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.14em', color: 'var(--hv-muted)' }}>CREDITS</span>
                        </div>
                    )}

                    <div style={{ marginTop: 20, width: '100%' }}>
                        <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.16em', color: 'var(--hv-muted)', marginBottom: 8 }}>VOUCHER CODE <span style={{ opacity: 0.7 }}>· OPTIONAL</span></div>
                        {voucher ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 46, padding: '0 8px 0 16px', borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)' }}>
                                <span style={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em' }}>{voucher.code}</span>
                                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>{voucher.bonusPercent != null ? `+${voucher.bonusPercent}% bonus credits` : `+${fmtNum(voucher.bonusCredits)} bonus credits`}</span>
                                <button type="button" onClick={removeVoucher} style={{ height: 30, padding: '0 12px', borderRadius: 999, border: 0, background: 'transparent', color: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    className="hv-input"
                                    value={voucherInput}
                                    onChange={(e) => { setVoucherInput(e.target.value.toUpperCase()); setVoucherErr(null); }}
                                    onKeyDown={(e) => e.key === 'Enter' && void applyVoucher()}
                                    placeholder="BONUS20"
                                    maxLength={20}
                                    style={{ flex: 1, minWidth: 0, height: 46, padding: '0 16px', borderRadius: 999, border: `1px solid ${voucherErr ? 'var(--hv-danger)' : 'var(--hv-line)'}`, background: 'var(--hv-card-2)', fontFamily: FONT_MONO, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-ink)', outline: 'none' }}
                                />
                                <button type="button" onClick={() => void applyVoucher()} disabled={!voucherInput.trim() || checking} style={{ flex: 'none', height: 46, padding: '0 18px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 13, fontWeight: 700, color: voucherInput.trim() ? 'var(--hv-ink)' : 'var(--hv-muted)', cursor: voucherInput.trim() ? 'pointer' : 'default' }}>{checking ? 'Checking…' : 'Apply'}</button>
                            </div>
                        )}
                        {voucherErr && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: 'var(--hv-danger)' }}>{voucherErr}</div>}
                    </div>

                    <div style={{ marginTop: 18, width: '100%', padding: 18, borderRadius: 18, background: 'var(--hv-card-2)', border: '1px dashed var(--hv-line-2)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: 'var(--hv-body)' }}><span>Subtotal</span><span>{fmtIDR(subtotal)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: 'var(--hv-body)' }}><span>Platform fee (10%)</span><span>{fmtIDR(fee)}</span></div>
                            {voucher && bonus > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 700, color: 'var(--hv-mint-ink)' }}><span>Voucher bonus</span><span>+{fmtNum(bonus)} credits</span></div>
                            )}
                        </div>
                        <div style={{ height: 1, background: 'var(--hv-line)', margin: '14px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.14em', color: 'var(--hv-muted)' }}>TOTAL</span>
                            <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--hv-ink)' }}>{fmtIDR(total)}</span>
                        </div>
                        {voucher && bonus > 0 && amount > 0 && (
                            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--hv-body)', textAlign: 'right' }}>You receive {fmtNum(amount + bonus)} credits. The bonus lands once the payment clears.</div>
                        )}
                    </div>

                    <div style={{ marginTop: 20, width: '100%', display: 'flex', gap: 10 }}>
                        <button type="button" onClick={onClose} className="hv-h-card2-ink" style={{ flex: 1, height: 48, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 13.5, fontWeight: 700, color: 'var(--hv-body)', cursor: 'pointer' }}>Cancel</button>
                        <button type="button" data-track="topup_pay" onClick={pay} disabled={busy || amount <= 0} className="hv-h-brand-dark" style={{ flex: 1.6, height: 48, borderRadius: 999, border: 0, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 14, fontWeight: 700, cursor: busy || amount <= 0 ? 'default' : 'pointer', opacity: busy || amount <= 0 ? 0.7 : 1 }}>
                            {busy ? 'Processing…' : 'Continue to payment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
